import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Descriptions, Button, Spin, message, List } from 'antd';
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import feeService, { Fee } from '../services/fee';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { getFileIconByType } from '../config/file-type-icons';
import mediaService, { MediaFile } from '../services/media';

// 最新报价详情页面：展示单条报价记录详细信息
const FeeDetail: React.FC = () => {
  useDocumentTitle('费用详情');
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [fee, setFee] = useState<Fee | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        const res = await feeService.getFee(Number(id));
        if (res.success && res.data) {
          setFee(res.data);
        } else {
          message.error(res.message || '无法获取报价详情');
        }
      } catch (e) {
        message.error('获取报价详情时发生错误');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const renderMoney = (v?: number | string) => {
    const num = typeof v === 'string' ? parseFloat(v) : v;
    return num && !isNaN(num) ? `¥${num.toFixed(2)}` : '-';
  };

  const hasField = (fieldKey: keyof Fee): boolean => {
    return !!fee && Object.prototype.hasOwnProperty.call(fee, fieldKey);
  };

  const basicItems = fee ? [
    { key: 'nation', label: '国家', span: 1, content: fee.nation || '-' },
    { key: 'service', label: '业务类型', span: 1, content: fee.service || '-' },
    { key: 'category_type', label: '制度/类别', span: 1, content: fee.category_type || '-' },
    { key: 'submit_channel', label: '递交渠道', span: 1, content: fee.submit_channel || '-' },
    { key: 'dates_submit', label: '递交周期', span: 1, content: fee.dates_submit || '-' },
    { key: 'dates_complete', label: '完成周期', span: 1, content: fee.dates_complete || '-' },
    { key: 'contact_info', label: '联系方式', span: 3, content: fee.contact_info || '-' },
    { key: 'remark', label: '备注', span: 3, content: <pre>{fee.remark || '-'}</pre> },
  ].filter(item => hasField(item.key as keyof Fee)) : [];

  const quoteItems = fee ? [
    { key: 'fee_official', label: '官费', content: renderMoney(fee.fee_official) },
    { key: 'fee_service', label: '服务费用', content: renderMoney(fee.fee_service) },
    { key: 'fee_inquiry', label: '查询费', content: renderMoney(fee.fee_inquiry) },
    { key: 'fee_notarization', label: '公证费', content: renderMoney(fee.fee_notarization) },
    { key: 'fee_cost', label: '成本', content: renderMoney(fee.fee_cost) },
    { key: 'fee_profit', label: '毛利润', content: renderMoney(fee.fee_profit) },
    { key: 'fee_peer', label: '同行报价', content: renderMoney(fee.fee_peer) },
  ].filter(item => hasField(item.key as keyof Fee)) : [];

  const documentItems = (fee?.documents_info || []).flatMap((d: any) => {
    const docId = d?.id;
    const medias = Array.isArray(d?.media_files_info) ? d.media_files_info : [];

    return medias.map((m: any) => {
      const idNum = Number(m.id);
      const mime = m.mime_type || m.file_type;
      const isImg = typeof mime === 'string' && mime.toLowerCase().startsWith('image');
      const url = mediaService.getMediaUrl(m);

      return {
        id: idNum,
        docId,
        name: m.name || m.file_name || `文件${idNum}`,
        file_size: m.file_size || m.size,
        mime_type: mime,
        file_type: m.file_type,
        is_image: isImg,
        url,
      };
    });
  });

  const canViewDocuments = hasField('documents') || hasField('documents_info');

  return (
    <div className={"fee-detail-view"} style={{ padding: 24 }}>
      <Card title="基本信息" extra={<Button onClick={() => navigate(-1)}>返回</Button>}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : (
          <Descriptions bordered size="middle" column={3}>
            {basicItems.map(item => (
              <Descriptions.Item key={item.key} label={item.label} span={item.span}>
                {item.content}
              </Descriptions.Item>
            ))}
          </Descriptions>
        )}
      </Card>

      {quoteItems.length > 0 && (
        <Card title="报价详情" style={{ marginTop: 16 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin />
            </div>
          ) : (
            <Descriptions bordered size="middle" column={3}>
              {quoteItems.map(item => (
                <Descriptions.Item key={item.key} label={item.label}>
                  {item.content}
                </Descriptions.Item>
              ))}
            </Descriptions>
          )}
        </Card>
      )}

      {/* 所需资料卡片 */}
      {canViewDocuments && (
        <Card title="所需资料" style={{ marginTop: 16 }}>
          {documentItems.length === 0 ? (
            <div style={{ color: '#999' }}>暂无资料</div>
          ) : (
            <List
              grid={{ gutter: 16, column: 6 }}
              dataSource={documentItems}
              renderItem={(m: any) => (
                <List.Item>
                  <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{ width: '100%', height: 120, backgroundColor: '#fafafa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {m.is_image && m.url ? (
                        <img src={m.url} alt={m.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ padding: 12, textAlign: 'center' }}>
                          <div style={{ fontSize: 28, marginBottom: 4 }}>
                            {getFileIconByType(mediaService.getFileTypeIcon({
                              original_name: m.name,
                              filename: m.name,
                              mime_type: m.mime_type
                            } as MediaFile))}
                          </div>
                          <div style={{ fontSize: 12, color: '#999' }}>{m.file_type || '文件'}</div>
                        </div>
                      )}
                    </div>
                    <div style={{ padding: '8px 8px 4px 8px' }} title={m.name}>
                      <div style={{ fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</div>
                      <div style={{ color: '#999', fontSize: 14 }}>{typeof m.file_size === 'number' ? `${(m.file_size/1024).toFixed(2)} KB` : ''}</div>
                    </div>
                    <div style={{ padding: '4px 8px 8px 8px', display: 'flex', gap: 8, justifyContent: 'flex-start' }}>
                      <a
                        href={m.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: 14, color: '#1890ff', display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <EyeOutlined /> 预览
                      </a>
                      <span
                        onClick={() => mediaService.downloadMedia(m.id, m.name)}
                        style={{ fontSize: 14, color: '#1890ff', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
                      >
                        <DownloadOutlined /> 下载
                      </span>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          )}
        </Card>
      )}
    </div>
  );
};

export default FeeDetail;
